// クラシック版の charat.common の道具（段階 C の2 の 7a。2026-10-26 に charat.common.js と画面ファイル gen2.js の viewTooltip から描き直した。動きは前と同じ）。
// SVG の文字からパーツの g を取り出す・パーツの差しかえの動き・色・グラデーション・柄・線の太さ・うすさ・回転・移動・拡大・反転（値を書くのは中身＝charat.core、描くのは charat.draw.js）、
// 座標と色の計算、メニューのボタンの動き、切り抜き、操作のヒントの吹き出し、SVG の入れ物づくり。charat.common に置く（charat.legacy.js・画面ファイル・ページは名前で呼ぶ）。
// d3 の選択・動き（transition）・scale と jQuery は fx.d3any・fx.jq でそのまま（7 で自前に）
import { chibi, common } from './engine.js';
import { screen } from './colors.js';
import * as fx from './fx.js';
import { S } from './sel.js';
import type { Ease } from './ease.js';
import * as jq from './jq.js';
import { tooltip } from './tooltip.js';

type Engine = Record<string, any>;
const E = (): Engine => chibi() as unknown as Engine;
const C = (): Record<string, any> => common() as unknown as Record<string, any>;
const draw = (): any => (window as unknown as { charatDraw: unknown }).charatDraw;
const SVG_SVG = 'svg';
const SVG_MASK = 'svgmask';

// ---- SVG の文字から ----

/** SVG の文字からルートの直下の要素の並び（文字を要素にして＝jQuery の $(文字) と同じ読み方、最初の svg の children。無ければ空の並び） */
export function getAllGfromSVG(anImg: unknown, _anId?: unknown): ArrayLike<Element> {
  let gList: ArrayLike<Element> = [];
  const mlist = jq.parseHTML(anImg);
  for (let i = 0; i < mlist.length; i++) {
    if (mlist[i].nodeName === SVG_SVG) {
      gList = (mlist[i] as Element).children;
      break;
    }
  }
  return gList;
}
/** SVG の文字から id の g（無ければ中身の無い g） */
export function getGfromSVG(anImg: unknown, id: string): Element {
  let gdata: Element | undefined;
  const mlist = jq.parseHTML(anImg);
  for (let i = 0; i < mlist.length; i++) {
    if (mlist[i].nodeName === SVG_SVG) {
      gdata = Array.prototype.find.call((mlist[i] as Element).children, (c: Element) => c.matches('#' + id)) as Element | undefined;
      break;
    }
  }
  if (gdata == undefined) {
    gdata = document.createElementNS('http://www.w3.org/2000/svg', 'g');
    gdata.setAttribute('id', id);
  }
  return gdata;
}
/** グラデーションの id をキャラ側だけ付けかえる */
export const replaceGrad = (aHtml: string): string => draw().replaceGrad(aHtml);

// ---- パーツの差しかえ ----

/**
 * パーツの差しかえ（パーツの一覧のボタン）: 差しかえ・パーツ番号・位置と拡大を戻す・色の塗り直しは aCorePart（中身の classic.part）。
 * ここでは差しかえの動き（aEffectNo 1: 1.1 倍から戻す、2: 下から入る）と後処理（afterChangeParts）。aCallback は差しかえのすぐあと（動きの前）
 */
export function changeParts(aSvgImage: any, aLayerKey: string, aLayer: string[], aEffectNo: number, aCallback: (() => void) | undefined, aCorePart: () => void): void {
  const c = C();
  c.nowChangeParts = true;
  aCorePart();
  c.nowChangeParts = false;
  if (aCallback != undefined) aCallback();
  const cw = aSvgImage.attr('width');
  const ch = aSvgImage.attr('height');
  if (aEffectNo == 1 && (typeof outlinemode === 'undefined' || !outlinemode)) {
    // 1.1 倍から戻す
    const csc = 1.1;
    aLayer.forEach((d) => {
      const layer = '#' + aLayerKey + d;
      jq.setAttr(layer, 'paramScale', 1);
      const scx = jq.attr(layer, 'paramScx') as unknown as number;
      const scy = jq.attr(layer, 'paramScy') as unknown as number;
      aSvgImage
        .select(layer + ' > g:not(.delete)')
        .attr('transform', () => 'translate(' + ((cw * csc - cw) * -0.45) / scx + ', ' + ((ch * csc - ch) * -0.4) / scy + '), scale(' + csc + ', ' + csc + ')')
        .transition()
        .duration(80)
        .attr('transform', () => 'translate(0, 0), scale(1, 1), rotate(0)')
        .on('end', () => {
          C().nowChangeParts = false;
        });
    });
  } else if (aEffectNo == 2) {
    // 下から入る
    aLayer.forEach((d) => {
      const layer = '#' + aLayerKey + d;
      aSvgImage
        .select(layer + ' > g:not(.delete)')
        .style('opacity', 0.5)
        .attr('transform', () => 'translate(0, ' + ch + '), scale(1, 1)')
        .transition()
        .duration(150)
        .ease(S.easeElastic)
        .style('opacity', 1)
        .attr('transform', () => 'translate(0, 0), scale(1, 1), rotate(0)')
        .on('end', () => {
          C().nowChangeParts = false;
        });
    });
  }
  c.afterChangeParts();
}
/** パーツの層の頭文字（aTgLayer[anIndex]。空ならどれも x）の層があるか */
export function checkLayer(aData: Record<string, unknown>, aTgLayer: string[], anIndex: number): boolean | undefined {
  if (aTgLayer.length == 0) return aData.x != undefined;
  if (aTgLayer[anIndex] == 'x') return aData.x != undefined;
  if (aTgLayer[anIndex] == 'y') return aData.y != undefined;
  if (aTgLayer[anIndex] == 'a') return aData.a != undefined;
  return undefined;
}
/** パーツの層の頭文字の層 */
export function getLayer(aData: Record<string, unknown>, aTgLayer: string[], anIndex: number): unknown {
  if (aTgLayer.length == 0) return aData.x;
  if (aTgLayer[anIndex] == 'x') return aData.x;
  if (aTgLayer[anIndex] == 'y') return aData.y;
  if (aTgLayer[anIndex] == 'a') return aData.a;
  return undefined;
}

// ---- 色・柄・線・うすさ ----

/** カテゴリの全レイヤーの色を変える（aSel 0: 左右 1: 左 2: 右。画面に向かって見たときの左右） */
export function changeColor(aSvgImage: any, aCategory: any, aLayerKey: string, aColor: string, aSel: number, aOrgColor?: unknown): void {
  if (aSvgImage == null) return;
  if (aCategory.COLOR[0] == undefined) return;
  aCategory.LAYER.forEach((d: string) => {
    draw().colorLayer(aSvgImage.select('#' + aLayerKey + d).node(), aCategory, aColor, aSel, aOrgColor);
  });
}
/** カテゴリの全レイヤーの色を反転する（aSel はどれでも同じ） */
export function changeRevColor(aSvgImage: any, aCategory: any, aLayerKey: string, aPtnRev: number, aColor1: string, aColor2: string, aStroke1: string, aStroke2: string, _aSel?: number): void {
  if (aSvgImage == null) return;
  aCategory.LAYER.forEach((d: string) => {
    draw().revColorLayer(aSvgImage.select('#' + aLayerKey + d).node(), aPtnRev, aColor1, aColor2, aStroke1, aStroke2);
  });
}
/** 要素の中のグラデーションの stop の色（offset ごとに colors） */
export function setGradColor(aSvgImage: string | Element, aOffset: unknown[], colors: string[]): void {
  aOffset.forEach((dd, ii) => {
    S
      .select(aSvgImage)
      .selectAll("linearGradient > stop[offset='" + dd + "']")
      .each(function (this: Element) {
        S.select(this).style('stop-color', colors[ii]);
      });
  });
}
/** 柄を defs（aDef。d3 の選択）に複製して色を付ける（作り方は charat.draw.js の makePattern） */
export function makePattern(aDef: any, sPattern: Element, aPtnDefId: string, aPtnRev: number | undefined, aColor: string, aRevCol1: string, aRevCol2: string, aRevStrokeCol1: string, aRevStrokeCol2: string, isDark: boolean): void {
  const defNode = aDef.node();
  if (!defNode) return;
  draw().makePattern(defNode, sPattern, aPtnDefId, aPtnRev, aColor, aRevCol1, aRevCol2, aRevStrokeCol1, aRevStrokeCol2, isDark, E().SIZE_PATTERN, E().ADJUST_PATTERN);
}
/** カテゴリの全レイヤーの線の太さ */
export function setStrokeWidth(aSvgImage: any, aCategory: any, aLayerKey: string, aWidth: string | number): void {
  if (aSvgImage == null) return;
  aCategory.LAYER.forEach((d: string) => {
    draw().strokeWidthLayer(aSvgImage.select('#' + aLayerKey + d).node(), aWidth);
  });
}
/** うすさ（スライダー）。値を書いて付けるのは中身 */
export function OpacityG(category: unknown, _layerAttrName: string, opacity: number): void {
  const coreOps = E().coreOps();
  if (coreOps) coreOps.opacity(category, opacity);
}
/** うすさのスライダー（#opacitySlider）を動かしたら、値を出してうすさを変える */
export function initOpacitySlider(): void {
  const slider = document.getElementById('opacitySlider') as HTMLInputElement;
  const sliderValueDisplay = document.getElementById('sliderValue')!;
  slider.addEventListener('input', () => {
    const opacity = parseFloat(slider.value);
    sliderValueDisplay.textContent = opacity as unknown as string;
    C().OpacityG(E().selCategory, E().ATTR_LAYER, opacity);
  });
}
/** うすさの欄の出し入れ（ページの opacityTarget のカテゴリのボタンで出し、ほかのカテゴリのボタンで隠す。宣言が無いメーカーは何もしない） */
export function toggleOpacityDisplay(): void {
  if (typeof opacityTarget === 'undefined') return;
  const opacityElement = S.select(E().DIV_F_OPACITY);
  opacityElement.style('display', 'none');
  (opacityTarget as string[]).forEach((buttonId) => {
    const targetButton = document.getElementById(buttonId)!;
    targetButton.addEventListener('click', () => {
      opacityElement.style('display', 'block');
    });
  });
  const pattern = /^chibiCategoryBtn\d+$/;
  document.querySelectorAll("[id^='chibiCategoryBtn']").forEach((button) => {
    if (!(opacityTarget as string[]).includes(button.id) && pattern.test(button.id)) {
      button.addEventListener('click', () => {
        opacityElement.style('display', 'none');
      });
    }
  });
}

// ---- 回転・移動・拡大・反転（値を書いて付けるのは中身。そのあと輪郭線の付け直し afterChangeTransform） ----

/** 回転の中心（カテゴリの全レイヤーを合わせた範囲の中心、500 の絵の座標） */
export function rotateCenter(aCategory: any, aLayerKey: string): { cx: number; cy: number } {
  return draw().rotateCenter(aCategory.LAYER.map((d: string) => S.select('#chaSvgSave #' + aLayerKey + d).node()));
}
/** 回転（paramRotate）が 0 でないレイヤーの変形を付け直す（読み込みの直後） */
export function rotateAllLayers(): void {
  const en = E();
  Object.values(en.allCategory as Record<string, any>).forEach((category) => {
    if (!(category.LAYER && Array.isArray(category.LAYER))) return;
    let center: { cx: number; cy: number } | null = null;
    category.LAYER.forEach((selectedLayer: string) => {
      const element = S.select('#chaSvgSave #' + en.ATTR_LAYER + selectedLayer);
      if (!element.node()) return;
      const rotate = Number(element.attr('paramRotate'));
      if (rotate == 0) return;
      const tx = Number(element.attr('paramTx'));
      const ty = Number(element.attr('paramTy'));
      const scx = Number(element.attr('paramScx'));
      const scy = Number(element.attr('paramScy'));
      const reverse = Number(element.attr('paramRev'));
      if (!center) center = C().rotateCenter(category, en.ATTR_LAYER);
      element.attr('transform', draw().layerTransform(en.scMake.x(tx + (reverse ? en.SIZE_CHARAT_IMAGE : 0)), en.scMake.y(ty), scx, scy, rotate, center));
    });
  });
}
export function rotateSelectedLayers(): void {
  C().rotateByDelta(5);
}
export function rotateSelectedLayersReverse(): void {
  C().rotateByDelta(-5);
}
/** 選んでいるカテゴリを degree 度回す */
export function rotateByDelta(degree: number): void {
  const coreOps = E().coreOps();
  if (!coreOps) return;
  coreOps.rotate(E().selCategory, degree);
  C().afterChangeTransform();
}
/** カテゴリを動かす（両方 0 なら位置を戻す）。ほかの引数は前の名残（呼ぶ所が多いので形は残す） */
export function moveG(aSvgImage: unknown, aCategory: unknown, _aLayerKey: string, anAddx: number, anAddy: number): void {
  const coreOps = E().coreOps();
  if (aSvgImage == null || aCategory == null || !coreOps) return;
  coreOps.move(aCategory, anAddx, anAddy);
  C().afterChangeTransform();
}
/** カテゴリを拡大・縮小する（aZoom ずつ、1±aZoomMax まで。aDuration は動きの長さ） */
export function zoomG(aSvgImage: unknown, aCategory: unknown, _aLayerKey: string, isZoom: boolean, _orgW: unknown, _orgH: unknown, _aFrameSize: unknown, aZoom: number, aZoomMax: number, aDuration?: number): void {
  const coreOps = E().coreOps();
  if (aSvgImage == null || aCategory == null || !coreOps) return;
  coreOps.zoom(aCategory, isZoom, aZoom, aZoomMax, aDuration);
  C().afterChangeTransform();
}
/** カテゴリを左右反転する・戻す（aDelay・aDuration は動き） */
export function reverseG(aSvgImage: unknown, aCategory: unknown, _aLayerKey: string, reverse: unknown, _aScaleX: unknown, _aScaleY: unknown, _aFrameSize: unknown, aDelay?: number, aDuration?: number): void {
  const coreOps = E().coreOps();
  if (aSvgImage == null || aCategory == null || !coreOps) return;
  coreOps.rev(aCategory, !!reverse, aDelay, aDuration);
}
/** カテゴリが反転しているか（どれかのレイヤーの paramRev が 1。同じ id ならページで最初の要素＝jQuery と同じ） */
export function getReverseG(aCategory: any, aLayerKey: string): boolean {
  let result = false;
  aCategory.LAYER.forEach((d: string) => {
    if ((jq.attr('#' + aLayerKey + d, 'paramRev') as unknown as number) == 1) result = true;
  });
  return result;
}
/** 選んでいるカテゴリを反転・戻す（サブメニューの反転のボタン） */
export function toggleReverseStatus(): void {
  const en = E();
  const toReverse = !C().getReverseG(en.selCategory, en.ATTR_LAYER);
  C().reverseG(en.svgMake, en.selCategory, en.ATTR_LAYER, toReverse, en.scMake.x, en.scMake.y, en.SIZE_CHARAT_IMAGE, 0, 100);
  en.btnSubReverse.STATUS = toReverse ? 1 : 0;
}

// ---- 座標・色の計算 ----

/** 番号（0〜）から並べたときの位置（aHorizon なら横に aWmax 個ずつ、偽なら縦に） */
export function getXYfromIndex(anIndex: number, _aRow: number, aW: number, aH: number, aWmax: number, aHorizon: boolean): { X: number; Y: number } {
  if (aHorizon) return { X: (anIndex % aWmax) * aW, Y: Math.floor(anIndex / aWmax) * aH };
  return { X: Math.floor(anIndex / aWmax) * aW, Y: (anIndex % aWmax) * aH };
}
export const addRgb = (rgbstr: string, r: number, g: number, b: number): string => draw().addRgb(rgbstr, r, g, b);
/** 色の 2 桁を増減 */
export const addColor = (col: string, addcol: number): string => ('0' + Math.max(0, Math.min(255, parseInt(col, 16) + addcol)).toString(16)).substr(-2);
export const convToHexRgb = (rgbstr: string): string => draw().toHex(rgbstr);
export const convToRgb = (rgbstr: string): string => draw().toRgb(rgbstr);
/** d3 の比例の scale（0〜domainMax を 0〜rangeMax に） */
export function getScale(domainMaxX: number, rangeMaxX: number, domainMaxY: number, rangeMaxY: number): { x: unknown; y: unknown } {
  const sc: { x: unknown; y: unknown } = { x: null, y: null };
  sc.x = S.scaleLinear().domain([0, domainMaxX]).range([0, rangeMaxX]);
  sc.y = S.scaleLinear().domain([0, domainMaxY]).range([0, rangeMaxY]);
  return sc;
}
export const inverseColor = (baseColor: string): string => draw().inverseColor(baseColor);
/** g の id からパーツ番号（4 桁。数でなければ -9999） */
export function getPartsNoFromG(idStr: string): number {
  let id = idStr.substring(1, 5);
  id = id.replace(/_/i, '');
  return !isNaN(id as unknown as number) ? Number(id) : -9999;
}

// ---- メニューのボタンの動き・切り抜き・吹き出し ----

/** メニューのボタンを横に動かし（aDur・anEase）、裏地の色を状態の色にする（aDur の倍） */
export function moveButton(anId: string, aPos: number, aDur: number, anEase: Ease): void {
  S
    .select(anId)
    .transition()
    .duration(aDur)
    .ease(anEase)
    .attr('transform', () => 'translate(' + aPos + ', 0)');
  S
    .select(anId + '>rect')
    .transition()
    .duration(aDur * 2)
    .style('fill', (d: any) => d.COLOR[d.BTN.STATUS]);
}
/** aMaskLayer の形で aMaskedLayerList のレイヤーを切り抜く（aClipdef は d3 の選択の defs） */
export function changeMask(_aSvgImage: unknown, aClipdef: any, aLayerKey: string, aMaskLayer: any, aMaskedLayerList: any[]): void {
  const msk = '#' + aLayerKey + aMaskLayer.LAYER[0];
  const maskid = SVG_MASK + aMaskLayer.LAYER[0];
  const gimg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  gimg.innerHTML = document.querySelector(msk + ' > g > g')!.innerHTML;
  aClipdef.select('#' + maskid).remove();
  aClipdef
    .append('clipPath')
    .attr('id', maskid)
    .append(() => gimg.children[0]);
  for (let i = 0; i < aMaskedLayerList.length; i++) {
    S
      .select('#' + aLayerKey + aMaskedLayerList[i].LAYER[0])
      .attr('clip-path', 'url(#' + maskid + ')');
  }
}
/**
 * 操作のヒントの吹き出し（tippy）。aOnce なら同じ anId は 1 回だけ（viewTooltipCount に覚える）。閉じたら tippy を捨てる。
 * 出方の動きは、第2世代（前の gen2.js）は TOOLTIP_ANIMATION（既定 "shift-away"）、ほかは "scale"
 */
export function viewTooltip(anId: string, aOnce: boolean, aSel: string, aPlacement: string, aContent: string, aDuration?: number[], aKeepOutline?: boolean): void {
  const c = C();
  let isShow = true;
  if (aOnce) {
    if (c.viewTooltipCount.indexOf(anId) >= 0) isShow = false;
    else c.viewTooltipCount.push(anId);
  }
  if (!isShow) return;
  if (aDuration == undefined) aDuration = [200, 100];
  tooltip(aSel, {
    content: aContent,
    interactive: true,
    placement: aPlacement,
    animation: screen() === 'gen2' ? E().config('TOOLTIP_ANIMATION', 'shift-away') : 'scale',
    animateFill: true,
    duration: aDuration,
    flip: false,
    showOnInit: true,
    sticky: true,
    arrow: true,
    arrowType: 'sharp',
    trigger: 'manual',
    theme: aPlacement + 'tipsmes',
    hideOnClick: true,
    zIndex: 9999,
    distance: 0,
    onHidden: () => {
      (document.querySelector(aSel) as Element & { _tippy: { destroy(): void } })._tippy.destroy();
    },
  });
  if (aKeepOutline != true) jq.setCss(aSel, 'outline', 'none');
}

// ---- SVG の入れ物 ----

/** aSel の中に svg を足して d3 の選択で返す（幅・高さを省くと aSel の大きさ。anId は # を取って id に） */
export function getD3SVG(aSel: string, anId: string, aW?: number, aH?: number): unknown {
  return S
    .select(aSel)
    .append('svg')
    .attr('width', aW !== undefined ? aW : jq.width(aSel))
    .attr('height', aH !== undefined ? aH : jq.height(aSel))
    .attr('id', anId.replace('#', ''));
}
/**
 * 作る画面のキャラの SVG の入れ物: ルートの svg の中に背景の svg（g#背景）、保存用の svg（g#clip > g#レイヤーの入れ物）、文字の g を作り、レイヤーの入れ物（d3 の選択）を返す。
 * スマホのアイコンのメーカー（ICONMODE）は寄せない
 */
export function getD3SVGWidhG(aSel: string, anSaveId: string, anBgSaveId: string, anLayerId: string, anTextId: string, anBgId: string, anClipId: string, aW: number | undefined, aH: number | undefined, aGW: number | undefined, aGH: number | undefined, aScale: number): unknown {
  const en = E();
  const mg = { x: (aW! - aGW!) / 2, y: (aH! * aScale - aGH!) / 3 };
  if (en.ICONMODE && en.isMobile) {
    mg.x = 0;
    mg.y = 0;
  }
  const mmakeg = { x: (aGW! - aGW! * aScale) / 2, y: (aGH! - aGH! * aScale) / 2 };
  aScale = 1;
  const W = (): number => jq.width(aSel), H = (): number => jq.height(aSel);
  const rootsvg = S
    .select(aSel)
    .append('svg')
    .attr('width', aW != undefined ? aW : W())
    .attr('height', aH != undefined ? aH : H());
  rootsvg
    .append('svg')
    .attr('width', aGW != undefined ? aW : W())
    .attr('height', aGH != undefined ? aH : H())
    .attr('id', anBgSaveId.replace('#', ''))
    .append('g')
    .attr('width', aGW != undefined ? aW : W())
    .attr('height', aGH != undefined ? aH : H())
    .attr('id', anBgId.replace('#', ''));
  const rootg = rootsvg
    .append('g')
    .attr('id', anSaveId.replace('#', '') + 'parent')
    .attr('transform', 'translate(' + mg.x + ',' + mg.y + '), scale(1,1)')
    .append('svg')
    .attr('width', aGW != undefined ? aGW : W())
    .attr('height', aGH != undefined ? aGH : H())
    .attr('id', anSaveId.replace('#', ''));
  const lg = rootg
    .append('g')
    .attr('width', aGW != undefined ? aGW : W())
    .attr('height', aGH != undefined ? aGH : H())
    .attr('transform', 'translate(' + mmakeg.x + ',' + mmakeg.y + '), scale(' + aScale + ',' + aScale + ')')
    .attr('id', anClipId.replace('#', ''))
    .append('g')
    .attr('width', aGW != undefined ? aGW : W())
    .attr('height', aGH != undefined ? aGH : H())
    .attr('id', anLayerId.replace('#', ''));
  rootsvg
    .append('g')
    .attr('width', aGW != undefined ? aW : W())
    .attr('height', aGH != undefined ? aH : H())
    .attr('id', anTextId.replace('#', ''));
  return lg;
}
